import React from 'react';

import { Onboarding } from '@/bpc/components/BaseComponents/Onboarding';
import {
  OnboardingGroupComponent,
  OnboardingGroupComponentProps,
} from '@/bpc/components/OnboardingGroupComponent/OnboardingGroupComponent';
import { OnboardingStatusComponent } from '@/bpc/components/OnboardingStatusComponent';
import { OnboardingGroup } from '@/bpc/components/OnboardingGroupComponent/OnboardingGroup';
import { connect } from 'react-redux';
import { Payload } from '@mjolnir/mf-fields-ts/src/Payload';
import { OnboardingSummaryComponent } from '@/bpc/components/OnboardingSummaryComponent';
import { BpcPayload, TermsAndConditionsPayload } from '@/bpc/types/BpcPayload';
import {
  getBpcState,
  getBusinessAwareArrayTranslator,
  getBusinessAwareTranslationKey,
  getBusinessAwareTranslator,
} from '@/selectors/bpc';
import { GROUP_OVERRIDES } from '@/bpc/overrides/groups';
import { ANALYTICS_EVENTS } from '@/bpc/utils/analyticsEvents';
import { ContractOnboardingStatus } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingStatus';
import { validateHasSensitiveGoods } from '@/bpc/utils/sensitiveGoods';
import { WithTermsAndConditionsProps } from '@/bpc/components/HOCs/WithTermsAndConditions';
import { ContractOnboardingType } from '@mjolnir/mf-fields-ts/src/enm/ContractOnboardingType';
import { getDecodedWidgetToken } from '@/utils/widget';
import { COMMON_GROUP_ID } from '@/bpc/utils/constants';
import {
  BPC_INSIGHT_EVENTS,
  createBPCInsightEventCategory,
  trackInsightsEvent,
} from '@/utils/analytics/insights';
import { normalizeString } from '@/utils/strings';

interface OnboardingComponentProps {
  onboardingKrn: string;
  initialPayload: Payload;
  fetchOnboarding: () => Promise<void>;
  patchOnboarding: (payload: Payload) => Promise<void>;
  initiateUnderwriting: () => Promise<void>;
  submitTermsAndConditions: (terms: TermsAndConditionsPayload) => Promise<void>;
  t: TranslatorFunction;
  bpcPayloadWithTerms: BpcPayload;
  // eslint-ignore-next-line @typescript-eslint/no-explicit-any
  tArray: ArrayTranslatorFunction<any>;
  tKey: TranslatorFunctionKey;
  entryPoint?: string;
}
interface OnboardingComponentState {
  onboarding: Onboarding;
  group: OnboardingGroup;
  showSummaryPage: boolean;
  showStatusPage: boolean;
  error?: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  isLoading: boolean;
}

export class _OnboardingComponent extends React.Component<
  OnboardingComponentProps,
  OnboardingComponentState
> {
  constructor(props: OnboardingComponentProps) {
    super(props);

    const onboarding = new Onboarding(
      props.onboardingKrn,
      props.initialPayload,
      this.props.patchOnboarding,
      this.props.initiateUnderwriting,
    );
    const group: OnboardingGroup = onboarding.firstGroup() || onboarding.lastGroup();

    const isStatusPending =
      this.props.bpcPayloadWithTerms.status === ContractOnboardingStatus.PENDING;
    const skipSummaryPage = this.shouldSkipSummaryPage();
    const doesNotHaveNextGroup = !group.hasNextGroup();
    const allMandatoryFieldsFilled = group.allMandatoryFilled();
    const sensitiveGoodsIsInvalid = !validateHasSensitiveGoods(group);

    const showSummaryPage =
      !skipSummaryPage &&
      isStatusPending &&
      doesNotHaveNextGroup &&
      allMandatoryFieldsFilled &&
      sensitiveGoodsIsInvalid;

    this.state = {
      onboarding,
      group,
      showSummaryPage,
      showStatusPage: ![
        ContractOnboardingStatus.PENDING,
        ContractOnboardingStatus.MISSING_ADDITIONAL_DATA,
      ].includes(this.props.bpcPayloadWithTerms.status),
      isLoading: false,
    };

    this.onPreviousGroup = this.onPreviousGroup.bind(this);
    this.onNextGroup = this.onNextGroup.bind(this);
    this.onNextSubgroup = this.onNextSubgroup.bind(this);
    this.getCurrentGroupId = this.getCurrentGroupId.bind(this);
    this.onShowSummaryPage = this.onShowSummaryPage.bind(this);
    this.onSummaryPageBack = this.onSummaryPageBack.bind(this);
    this.onSubmitApplication = this.onSubmitApplication.bind(this);
    this.logExitPoint = this.logExitPoint.bind(this);
    this.logStatusEvent = this.logStatusEvent.bind(this);
    this.createMissingAdditionalDataGroup = this.createMissingAdditionalDataGroup.bind(this);
  }

  private createMissingAdditionalDataGroup(): OnboardingGroup {
    return this.state.onboarding.createMissingAdditionalDataGroup(
      this.props.bpcPayloadWithTerms.missing_additional_data as string[],
    );
  }

  private onPreviousGroup(): void {
    const { group } = this.state;
    if (group.hasPreviousGroup()) {
      const prevGroup = group.previousGroup();
      this.state.onboarding.logEvent(ANALYTICS_EVENTS.backButton, {
        currentGroupId: group.groupId,
        destinationGroupId: prevGroup.groupId,
      });
      this.setState({ group: prevGroup });
    }
  }

  private async onNextGroup(): Promise<void> {
    const group = this.state.group.nextGroup();
    this.setState({ isLoading: true });
    try {
      await this.state.onboarding.pushPayloadToBackendAndTriggerContinueEvent(
        this.state.group.groupId,
      );
      this.setState({
        error: undefined,
        // this group change handles the navigation
        group,
        isLoading: false,
      });
    } catch (error) {
      this.setState({ error, isLoading: false });
    }
  }

  private async onShowSummaryPage(): Promise<void> {
    try {
      await this.state.onboarding.pushPayloadToBackendAndTriggerContinueEvent(
        this.state.group.groupId,
      );
    } catch (error) {
      this.setState({ error });
    }

    this.setState({
      error: undefined,
      showSummaryPage: true,
    });
  }

  private onSummaryPageBack() {
    this.state.onboarding.logEvent(ANALYTICS_EVENTS.backButton, {
      currentGroupId: COMMON_GROUP_ID.SUMMARY_PAGE,
      destinationGroupId: this.state.group.groupId,
    });
    this.setState({
      showSummaryPage: false,
    });
  }

  private makeBpcInsightEvent(suffix?: string): string {
    const type = this.state.onboarding.getContractOnboardingType();
    const category = !suffix ? type : `${type}/${suffix}`;
    const bpcEventCategory = createBPCInsightEventCategory(category);

    return normalizeString(bpcEventCategory);
  }

  private async submitTermsAndConditions(terms: TermsAndConditionsPayload) {
    trackInsightsEvent({
      category: this.makeBpcInsightEvent(BPC_INSIGHT_EVENTS.TERMS_AND_CONDITIONS_SUBMITTED),
      action: 'click',
    });
    return this.props.submitTermsAndConditions(terms);
  }

  private onSubmitApplication() {
    this.state.onboarding.logEvent(ANALYTICS_EVENTS.applicationSubmitted, {});
    trackInsightsEvent({
      category: this.makeBpcInsightEvent(BPC_INSIGHT_EVENTS.APPLICATION_SUBMITTED),
      action: 'click',
    });
    this.setState({
      showSummaryPage: false,
      showStatusPage: true,
    });
  }

  private async onNextSubgroup(): Promise<void> {
    try {
      await this.state.onboarding.pushPayloadToBackendAndTriggerContinueEvent(
        this.state.group.groupId,
      );
      this.setState({ error: undefined });
    } catch (error) {
      this.setState({ error });
    }
  }

  private getCurrentGroupId(): string {
    if (this.state.showStatusPage) {
      return COMMON_GROUP_ID.STATUS_PAGE;
    }
    if (this.state.showSummaryPage) {
      return COMMON_GROUP_ID.SUMMARY_PAGE;
    }
    return String(this.state.group.groupId);
  }

  private logExitPoint(exitPoint: string) {
    this.state.onboarding.logEvent(ANALYTICS_EVENTS.exitPointClicked, { exitPoint });
  }

  private logStatusEvent(oldStatus: string | undefined = 'No previous status', newStatus: string) {
    this.state.onboarding.logEvent(ANALYTICS_EVENTS.statusChanged, { oldStatus, newStatus });
  }

  public componentDidMount(): void {
    const currentGroupId = this.getCurrentGroupId();
    this.state.onboarding.logEvent(ANALYTICS_EVENTS.appLoaded, { currentGroupId });
    this.state.onboarding.logEvent(ANALYTICS_EVENTS.pageView, { currentGroupId });
    trackInsightsEvent({
      category: this.makeBpcInsightEvent(currentGroupId),
      action: 'impression',
    });
  }

  public componentDidUpdate(
    _prevProps: Readonly<OnboardingComponentProps>,
    prevState: Readonly<OnboardingComponentState>,
  ): void {
    if (
      this.state.showSummaryPage !== prevState.showSummaryPage ||
      this.state.showStatusPage !== prevState.showStatusPage ||
      this.state.group.groupId !== prevState.group.groupId
    ) {
      const currentGroupId = this.getCurrentGroupId();
      this.state.onboarding.logEvent(ANALYTICS_EVENTS.pageView, {
        currentGroupId,
      });
      trackInsightsEvent({
        category: this.makeBpcInsightEvent(currentGroupId),
        action: 'impression',
      });
    }
  }

  private shouldSkipSummaryPage(): boolean {
    const CONTRACT_TYPES_WITHOUT_SUMMARY_PAGE = [
      ContractOnboardingType.UPSELL_WEBSTORE_TO_ADS_MANAGER,
      ContractOnboardingType.UPSELL_WEBSTORE_TO_CONTENT_MANAGER,
    ];

    const contractOnboardingType = this.props.bpcPayloadWithTerms.data?.contract_onboarding_type;

    return !!(
      contractOnboardingType && CONTRACT_TYPES_WITHOUT_SUMMARY_PAGE.includes(contractOnboardingType)
    );
  }

  render(): React.ReactNode {
    const { error, group, onboarding, showStatusPage, showSummaryPage } = this.state;
    const {
      bpcPayloadWithTerms,
      entryPoint,
      fetchOnboarding,
      submitTermsAndConditions,
      t,
      tArray,
      tKey,
    } = this.props;
    const showGroupComponent = !showSummaryPage && !showStatusPage;
    const hasGroupOverride = !!GROUP_OVERRIDES[group.groupId];
    const GroupComponent: React.ComponentType<OnboardingGroupComponentProps &
      WithTermsAndConditionsProps> = hasGroupOverride
      ? GROUP_OVERRIDES[group.groupId]
      : OnboardingGroupComponent;

    return (
      <>
        {showSummaryPage && (
          <OnboardingSummaryComponent
            error={error}
            group={group}
            payload={onboarding.payload}
            onSummaryPageBack={this.onSummaryPageBack}
            onSubmit={this.onSubmitApplication}
            onboarding={onboarding}
            bpcPayloadWithTerms={bpcPayloadWithTerms}
            t={t}
            submitTermsAndConditions={submitTermsAndConditions}
          />
        )}
        {showStatusPage && (
          <OnboardingStatusComponent
            error={error}
            fetchOnboarding={fetchOnboarding}
            logExitPoint={this.logExitPoint}
            logStatusEvent={this.logStatusEvent}
          />
        )}
        {showGroupComponent && (
          <GroupComponent
            error={this.state.error}
            group={
              bpcPayloadWithTerms.status === ContractOnboardingStatus.MISSING_ADDITIONAL_DATA
                ? this.createMissingAdditionalDataGroup()
                : group
            }
            t={t}
            tArray={tArray}
            tKey={tKey}
            hasPreviousGroup={group.hasPreviousGroup()}
            hasNextGroup={group.hasNextGroup()}
            onPreviousGroup={this.onPreviousGroup}
            onNextGroup={this.onNextGroup}
            onNextSubgroup={this.onNextSubgroup}
            onShowSummaryPage={this.onShowSummaryPage}
            isLoading={this.state.isLoading}
            bpcPayloadWithTerms={bpcPayloadWithTerms}
            submitTermsAndConditions={submitTermsAndConditions}
            onboarding={onboarding}
            onSubmit={this.onSubmitApplication}
            shouldSkipSummaryPage={this.shouldSkipSummaryPage()}
            entryPoint={entryPoint}
          />
        )}
      </>
    );
  }
}

export const OnboardingComponent = connect(state => ({
  bpcPayloadWithTerms: getBpcState(state),
  t: getBusinessAwareTranslator(state),
  tArray: getBusinessAwareArrayTranslator(state),
  tKey: getBusinessAwareTranslationKey(state),
  entryPoint: getDecodedWidgetToken()?.entryPoint,
}))(_OnboardingComponent);
